import React, { useEffect, useMemo, useState } from "react";
import { Maximize2, X } from "lucide-react";
import { renderPdfThumbnailUrl } from "@/components/extend/pdf-thumbnail-utils";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

import {
  citationHighlightRect,
  citationSnippetOffset,
  renderedPageHeightFor,
  type CitationGeometry
} from "./citationHighlight";

/**
 * A page is rendered wider than the card so the text is readable at all, then
 * cropped. The viewport is the card's inner width less its padding.
 */
const SNIPPET_PAGE_WIDTH = 760;
const SNIPPET_VIEWPORT_WIDTH = 352;
const SNIPPET_VIEWPORT_HEIGHT = 116;
/** A 360px hover card less its padding and the evidence block's. */
const COMPACT_VIEWPORT_WIDTH = 316;
const COMPACT_VIEWPORT_HEIGHT = 96;

type Props = {
  file: string;
  geometry: CitationGeometry;
  /** 1-based, as Extend reports it. */
  page: number;
  filename?: string | null;
  /** Whether the docked panel is currently showing this citation. */
  isExpanded?: boolean;
  onExpand: () => void;
  /**
   * 96px tall with no View Page label over the crop: the caller puts that
   * action in its own source row.
   */
  compact?: boolean;
};

/**
 * The cited region of the document, cropped to the value and highlighted.
 *
 * The page renders at a fixed width and is shifted behind a short clipping box,
 * rather than being scaled down to fit: a whole page in a 420px chat panel is
 * illegible, and the point is for the client to read the line their answer came
 * from without leaving the conversation.
 *
 * The page is drawn once as an image by the shared PDF engine rather than in
 * `PdfPreview`, whose zoom and pagination chrome is both unusable and unwanted
 * at this size.
 */
export function CitationSnippet({
  file,
  geometry,
  page,
  filename,
  isExpanded = false,
  onExpand,
  compact = false
}: Props) {
  const pageImageUrl = usePageImage(file, page - 1, SNIPPET_PAGE_WIDTH);
  const viewportWidth = compact
    ? COMPACT_VIEWPORT_WIDTH
    : SNIPPET_VIEWPORT_WIDTH;
  const viewportHeight = compact
    ? COMPACT_VIEWPORT_HEIGHT
    : SNIPPET_VIEWPORT_HEIGHT;
  const renderedPageHeight = renderedPageHeightFor(
    geometry,
    SNIPPET_PAGE_WIDTH
  );
  const rect = useMemo(
    () =>
      renderedPageHeight === null
        ? null
        : citationHighlightRect(geometry, {
            renderedPageWidth: SNIPPET_PAGE_WIDTH,
            renderedPageHeight
          }),
    [geometry, renderedPageHeight]
  );

  if (rect === null || renderedPageHeight === null) return null;

  const { offsetX, offsetY } = citationSnippetOffset({
    rect,
    viewportWidth,
    viewportHeight,
    renderedPageWidth: SNIPPET_PAGE_WIDTH,
    renderedPageHeight
  });
  const placeholder = compact ? (
    <Skeleton
      className="rounded-none bg-fill-2"
      style={{ width: SNIPPET_PAGE_WIDTH, height: viewportHeight }}
    />
  ) : (
    <SnippetPlaceholder />
  );

  return (
    <button
      type="button"
      onClick={onExpand}
      aria-pressed={isExpanded}
      aria-label={
        filename ? `View this in ${filename}` : "View this in the document"
      }
      // Marked while its document is the one docked beside the chat: the panel
      // outlives the click that opened it, so several snippets can be on screen
      // with only one of them being the thing shown.
      className={cn(
        "group relative block w-full overflow-hidden bg-elevated ring-1 transition-shadow duration-100 focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:outline-none",
        compact ? "rounded-md" : "rounded-lg",
        isExpanded
          ? "ring-line-4"
          : compact
          ? "ring-line-1 hover:ring-line-3"
          : "ring-line-2 hover:ring-line-3"
      )}
      style={{ height: viewportHeight }}
    >
      <div
        className="absolute left-0 top-0"
        style={{ transform: `translate(${offsetX}px, ${offsetY}px)` }}
        data-testid="citation-snippet-page"
      >
        {pageImageUrl ? (
          <img
            src={pageImageUrl}
            alt=""
            draggable={false}
            className="block max-w-none"
            style={{ width: SNIPPET_PAGE_WIDTH }}
          />
        ) : (
          placeholder
        )}
        <span
          aria-hidden="true"
          className="pointer-events-none absolute rounded-xs bg-warn/30 ring-1 ring-warn/70"
          style={{
            left: rect.left,
            top: rect.top,
            width: rect.width,
            height: rect.height
          }}
        />
      </div>

      {compact ? null : (
        <span
          className={cn(
            "absolute inset-x-0 bottom-0 flex items-center justify-end gap-1 bg-linear-to-t from-elevated via-elevated/80 to-transparent px-2 pt-4 pb-1 text-sm font-medium text-fg-2 transition-opacity duration-100 group-hover:opacity-100 group-focus-visible:opacity-100",
            isExpanded ? "opacity-100" : "opacity-0"
          )}
        >
          {isExpanded ? (
            <X className="size-3.5" aria-hidden />
          ) : (
            <Maximize2 className="size-3.5" aria-hidden />
          )}
          {isExpanded ? "Hide Page" : "View Page"}
        </span>
      )}
    </button>
  );
}

/**
 * The page rendered to an image URL. The panel's own error handling covers the
 * fetch; a render failure here just leaves the placeholder, and the quote below
 * it stands.
 */
function usePageImage(file: string, pageIndex: number, width: number) {
  const [rendered, setRendered] = useState<{
    key: string;
    url: string | null;
  } | null>(null);
  const key = `${file}#${pageIndex}@${width}`;
  useEffect(() => {
    let cancelled = false;
    renderPdfThumbnailUrl({ url: file, pageIndex, width }).then(
      (url) => {
        if (!cancelled) setRendered({ key, url });
      },
      () => {
        if (!cancelled) setRendered({ key, url: null });
      }
    );
    return () => {
      cancelled = true;
    };
  }, [file, key, pageIndex, width]);
  return rendered?.key === key ? rendered.url : null;
}

function SnippetPlaceholder() {
  return (
    <div
      className="animate-pulse bg-fill-1"
      style={{ width: SNIPPET_PAGE_WIDTH, height: SNIPPET_VIEWPORT_HEIGHT }}
    />
  );
}
